import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { EMAIL_NOT_VERIFIED_MESSAGE, formatCents, type ReferralsResponse } from '@nexus/shared';

import { useSession } from '../auth/SessionContext';
import { ApiError, apiGet } from '../lib/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  LoadErrorPage,
  LoadingPage,
  Money,
  PageHeader,
  Stat,
  StatGrid,
  When,
  type Column,
} from '../ui';

/**
 * Referrals.
 *
 * ## The three numbers, and why "qualified" is one of them
 *
 * Referrals is how many friends used the code. Qualified is how many of them
 * are actually earning — approved by the operator, with at least one earning
 * that has come through the holdback. Earned is the money.
 *
 * Showing only the first and the last would leave a creator with four referrals
 * and $0 staring at a screen that offers no explanation. The middle number is
 * that explanation: nought of four qualified means nobody has been paid yet,
 * which is a different problem from four of four qualified and nought dollars.
 *
 * ## The rules are on the page, in the creator's words
 *
 * Not behind a link and not in a tooltip. A creator who is about to send this
 * link to a friend is making a claim on that friend's behalf, and the three
 * things they need to have right are the rate, the ceiling, and WHEN it counts
 * — because "my friend signed up and I got nothing" is the support conversation
 * this paragraph exists to prevent.
 */
export function Referrals(): React.JSX.Element {
  const [data, setData] = useState<ReferralsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);
  const { session } = useSession();
  /* `GET /referrals` ISSUES the code on first read, and the API refuses that
     to an unconfirmed address (a code from a throwaway account is the
     cheapest way to farm bonuses). Said here, in the API's sentence, rather
     than shown as a failed load. */
  const emailVerified = session?.user.emailVerified !== false;

  const load = useCallback(async () => {
    if (!emailVerified) return;
    try {
      setData(await apiGet<ReferralsResponse>('/referrals'));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your referrals.');
    }
  }, [emailVerified]);

  useEffect(() => {
    void load();
  }, [load]);

  function copy(what: 'code' | 'link', value: string): void {
    void navigator.clipboard?.writeText(value);
    setCopied(what);
  }

  if (!emailVerified) {
    return (
      <>
        <PageHeader
          eyebrow="Creator"
          title="Referrals"
          lead="Share Nexus with a friend and earn a share of what they earn."
        />
        <Alert tone="waiting" title="Confirm your email first">
          {EMAIL_NOT_VERIFIED_MESSAGE}
        </Alert>
      </>
    );
  }

  if (error && data === null) {
    return (
      <LoadErrorPage
        eyebrow="Creator"
        title="Referrals"
        heading="Could not load your referrals."
        onRetry={() => void load()}
      >
        <span>{error} Nothing you have earned is affected.</span>
      </LoadErrorPage>
    );
  }

  if (data === null) return <LoadingPage eyebrow="Creator" title="Referrals" stats />;

  return (
    <>
      <PageHeader
        eyebrow="Creator"
        title="Referrals"
        lead={`Share Nexus with a friend and earn ${data.ratePercent}% of what they earn, up to ${formatCents(data.capCentsPerReferee)} per friend.`}
      />

      {/* The code and the figures are one decision — "is this worth sharing" —
          and they were two full-width cards, the second of which pushed the table
          of friends off the first screen. */}
      <div className="split even">
        <Card variant="feature" title="Your link">
        <p className="muted">
          Anybody who signs up with this link — or types the code on the signup form — is your
          referral from then on.
        </p>

        <p>
          <code className="chip" data-testid="referral-code">
            {data.code}
          </code>
        </p>

        <p>
          <code className="chip quiet" data-testid="referral-link">
            {data.link}
          </code>
        </p>

        <div className="actions">
          <Button variant="primary" onClick={() => copy('link', data.link)}>
            {copied === 'link' ? 'Copied' : 'Copy the link'}
          </Button>
          <Button onClick={() => copy('code', data.code)}>
            {copied === 'code' ? 'Copied' : 'Copy the code'}
          </Button>
        </div>
      </Card>

      <Card variant="feature">
        <StatGrid>
          <Stat
            label="Referrals"
            value={data.totals.referred}
            note="Friends who signed up with your code."
          />
          <Stat
            label="Qualified"
            value={data.totals.qualified}
            note="Approved, and earning."
          />
          <Stat
            label="Earned from referrals"
            value={<Money cents={data.totals.earnedLifetimeCents} />}
            note="Lifetime, before anything was paid out."
            emphasis
          />
        </StatGrid>

        {/* The same money, in the four states the Wallet shows it in, because a
            creator who reads one figure here and a different one there stops
            believing both. */}
        {data.totals.earnedLifetimeCents > 0 && (
          <p className="muted">
            Of that, <Money cents={data.totals.availableCents} /> is available now,{' '}
            <Money cents={data.totals.pendingCents} /> is still pending,{' '}
            <Money cents={data.totals.inPayoutCents} /> is inside a payout and{' '}
            <Money cents={data.totals.paidCents} /> has been paid. It is all in your{' '}
            <Link to="/wallet">wallet</Link> — a referral bonus is paid exactly like any other
            earning.
          </p>
        )}
        </Card>
      </div>

      <Card title="How it works">
        <p>
          You earn <strong>{data.ratePercent}%</strong> of every earning your friend makes, up to{' '}
          <strong>{formatCents(data.capCentsPerReferee)} per friend</strong> for as long as they
          keep posting — ten friends is a ceiling of {formatCents(data.capCentsPerReferee * 10)},
          not of {formatCents(data.capCentsPerReferee)}. A bonus is added the moment your friend&rsquo;s
          earning becomes payable, which is after its 72-hour holdback and only if no check is
          holding it — not when they post, and not when their views are counted. A friend counts as{' '}
          <em>qualified</em> once the operator has approved them and at least one of their earnings
          has come through that holdback. If one of their posts comes down afterwards, their earning
          is voided and the bonus that came from it is reversed with it. Your own code cannot refer
          you, and a code has to be used at signup — it cannot be added to an account later.
        </p>
      </Card>

      <Card title="Your referrals" sub="Every friend who used your code, and what each has been worth.">
        <RefereeTable data={data} />
        <p className="muted">
          A friend who signed up with your code and is not listed, or a bonus that never came?{' '}
          <Link to="/support/new?category=REFERRAL" data-testid="report-referral-issue">
            Report a referral issue
          </Link>{' '}
          and a person will look.
        </p>
      </Card>

      {data.referredBy && (
        <Card tight>
          <p className="muted">
            You were referred by {data.referredBy.displayName} on{' '}
            <When value={data.referredBy.at} />.
          </p>
        </Card>
      )}

      {error && <Alert tone="critical">{error}</Alert>}
    </>
  );
}

function RefereeTable({ data }: { data: ReferralsResponse }): React.JSX.Element {
  type Row = ReferralsResponse['referees'][number];
  const columns: Array<Column<Row>> = [
    {
      key: 'who',
      header: 'Friend',
      primary: true,
      /* The masked name, which is all this response carries. There is no email
         field on the contract at all, which is the only way to be sure the next
         edit of this table does not add one. */
      cell: (row) => row.displayName,
    },
    {
      key: 'status',
      header: 'Status',
      /* Four states, and only one of them is green. An APPROVED friend who has
         not earned yet is NEUTRAL and not positive: the whole job of this column
         is to tell the two apart, and `statusTone` would paint both the same
         because it is answering a different question — is this account in good
         standing — than the one the referrer is asking.

         SUSPENDED and REJECTED are their own state and not "waiting on
         approval": a friend the operator has stopped is never going to be
         approved by waiting, and a referrer reading "waiting" goes on expecting
         money that is not coming. What they have already earned stands — the
         bonus on an earning that was already payable is not clawed back — which
         is why this says "no longer earning" rather than anything about the
         figures in the two columns beside it. */
      cell: (row) =>
        row.qualified ? (
          <Badge tone="positive">Qualified</Badge>
        ) : row.status === 'SUSPENDED' || row.status === 'REJECTED' ? (
          <Badge tone="critical">No longer earning</Badge>
        ) : row.status === 'APPROVED' ? (
          <Badge tone="neutral">Not earning yet</Badge>
        ) : (
          <Badge tone="waiting">Waiting on approval</Badge>
        ),
    },
    { key: 'joined', header: 'Joined', cell: (row) => <When value={row.joinedAt} /> },
    {
      key: 'earned',
      header: 'Earned you',
      numeric: true,
      align: 'end',
      cell: (row) => <Money cents={row.earnedCents} />,
    },
    {
      key: 'remaining',
      header: 'Left of the cap',
      numeric: true,
      align: 'end',
      cell: (row) => <Money cents={row.capRemainingCents} />,
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={data.referees}
      getKey={(row) => row.creatorId}
      empty={
        <EmptyState title="Nobody has used your code yet.">
          Send the link to somebody who cuts clips. You earn from their first payable earning
          onwards, not from their signup.
        </EmptyState>
      }
    />
  );
}
